import { isDefined } from '@/utils/general'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { BBDTag, GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Popover } from 'antd'
import { startCase } from 'lodash'

type PayrollLinkPopoverProps = {
  linkedInfo: Gravitate.LinkedFreightLineItem
  timezone: string
  type: 'accessorial' | 'special pay'
}

export function PayrollLinkPopover({ linkedInfo, timezone, type }: PayrollLinkPopoverProps) {
  if (!linkedInfo) return null
  const isOutOfSync = linkedInfo.status === 'Out of Sync'
  const isManualOrAutomatic = linkedInfo.status === 'manual' || linkedInfo.status === 'automatic'
  return (
    <Popover
      trigger={'hover'}
      styles={{ root: { maxWidth: '300px' } }}
      title={
        <Horizontal justifyContent='space-between'>
          <Texto weight={'bold'}>{type === 'accessorial' ? 'Accessorial Link' : 'Special Pay Link'}</Texto>
          {linkedInfo?.status && (
            <BBDTag style={{ flex: 'none' }} error={isOutOfSync} success={linkedInfo.status === 'approved'} theme2={isManualOrAutomatic} >{startCase(linkedInfo.status)}</BBDTag>
          )}
        </Horizontal>
      }
      content={<PopoverContent linkedInfo={linkedInfo} timezone={timezone} />}
    >
      <GraviButton
        style={{ color: isOutOfSync ? 'var(--theme-error)' : 'var(--theme-color-2)' }}
        size='small'
        icon={<MaterialIcon icon='receipt' size={16} />}
        appearance='text'
      />
    </Popover>
  )
}

function PopoverContent({ linkedInfo, timezone }: Omit<PayrollLinkPopoverProps, 'type'>) {
  return (
    <Vertical gap={8} style={{ minWidth: 250 }} className='pt-2'>
      {Object.entries(linkedInfo)
        .filter(([key]) => key !== 'status')
        .map(([key, value]) => (
          <Vertical key={key}>
            <Texto weight={'bold'}>{getFieldName(key)}</Texto>
            <Texto>{getFieldValue(key, value, timezone)}</Texto>
          </Vertical>
        ))}
    </Vertical>
  )
}

function getFieldName(key: string): string {
  switch (key) {
    case 'book_type':
      return 'Type'
    case 'updated_by':
      return 'Created By'
    case 'updated_on':
      return 'Created Date/Time'
    default:
      return startCase(key.replace(/_/g, ' '))
  }
}

const dateFields = ['updated_on', 'created_on', 'date', 'approved_on']
function getFieldValue(key: string, value: string | number | null | undefined, timezone: string): string {
  if (!isDefined(value)) return '-'
  if (dateFields.includes(key)) return formatters.DATE_TIME(value as string, timezone) as string
  return value as string
}

function getStatusTagStyles(linkedInfo: Gravitate.LinkedFreightLineItem) {
  if (!linkedInfo) return {}
  let styles = {}
  if (linkedInfo.status === 'Out of Sync') {
    styles = {
      color: 'var(--theme-error)',
      border: '1px solid var(--theme-error)',
      backgroundColor: 'var(--theme-error-dim)',
    }
  } else if (linkedInfo.status === 'approved') {
    styles = {
      color: 'var(--theme-success)',
      border: '1px solid var(--theme-success)',
      backgroundColor: 'var(--theme-success-dim)',
    }
  } else if (linkedInfo.status === 'manual' || linkedInfo.status === 'automatic') {
    styles = {
      color: 'var(--theme-color-2)',
      border: '1px solid var(--theme-color-2)',
      backgroundColor: 'var(--theme-color-2-dim)',
    }
  }
  return styles
}
